import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiClock, FiUser, FiArrowLeft, FiShare2, FiHeart, FiPlay, FiCalendar, FiMapPin, FiX } from 'react-icons/fi';
import { FaYoutube } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { VolumeX, Volume2 } from 'lucide-react';
import { fetchSpeechById, fetchSpeeches, getImageSrc, formatMarathiDate } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const SpeechDetails = () => {
  const { language } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [speech, setSpeech] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedSpeeches, setRelatedSpeeches] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    loadSpeech();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (speech) {
      loadRelatedSpeeches();
    }
  }, [speech]);

  const loadSpeech = async () => {
    try {
      setLoading(true);
      const speechData = await fetchSpeechById(id);
      if (speechData) {
        setSpeech(speechData);
      } else {
        setError(language === 'MR' ? 'भाषण सापडले नाही' : 'Speech not found');
      }
    } catch (err) {
      console.error('Error loading speech:', err);
      setError(language === 'MR' ? 'भाषण लोड करताना त्रुटी' : 'Failed to load speech');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedSpeeches = async () => {
    try {
      const speechesData = await fetchSpeeches();
      const filtered = speechesData.filter(relatedSpeech => relatedSpeech._id !== id);
      setRelatedSpeeches(filtered.slice(0, 3));
    } catch (err) {
      console.error('Error loading related speeches:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'MR'
      ? date.toLocaleDateString('mr-IN', {
          weekday: 'long',
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        })
      : date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long', 
          day: 'numeric'
        });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: speech.title,
          text: speech.content[0] || '',
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert(language === 'MR' ? 'लिंक क्लिपबोर्डवर कॉपी केले!' : 'Link copied to clipboard!');
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const handlePlayVideo = () => {
    setShowVideoModal(true);
    setIsVideoPlaying(true);
    setIsMuted(true);
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setIsVideoPlaying(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4 w-1/4"></div>
              <div className="h-12 bg-gray-300 rounded mb-6"></div>
              <div className="h-64 bg-gray-300 rounded mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !speech) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-3xl font-bold text-red-800 mb-4">
            {language === 'MR' ? 'भाषण सापडले नाही' : 'Speech not found'}
          </h2>
          <p className="text-red-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate(-1)}
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors"
            >
              {language === 'MR' ? 'मागे जा' : 'Go back'}
            </button>
            <Link 
              to="/allspeeches"
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors inline-block"
            >
              {language === 'MR' ? 'सर्व भाषणे' : 'All Speeches'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title={speech.title} description={speech.content?.[0] ? speech.content[0].replace(/<[^>]+>/g, '').slice(0, 155) : 'डॉ. राजेंद्र शेंडे यांचे भाषण.'} image={speech.thumbnail} canonical={`/speech/${speech.slug || speech._id}`} type="article" article={{ publishedTime: speech.date }} breadcrumbs={[{ name: 'मुख्यपृष्ठ', url: '/' }, { name: 'भाषणे', url: '/allspeeches' }, { name: speech.title, url: `/speech/${speech.slug || speech._id}` }]} />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-medium transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              {language === 'MR' ? 'मागे जा' : 'Go back'}
            </button>
          </motion.div>

          {/* Speech Header */}
          <motion.article 
            className="bg-white border-2 border-red-100 rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Hero Image */}
            <div className="relative h-64 md:h-96 overflow-hidden">
              <img loading="lazy" 
                src={getImageSrc(speech.thumbnail)} 
                alt={speech.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Video Indicator */}
              {speech.videoLink && (
                <div className="absolute top-6 right-6">
                  <div className="bg-red-800 text-white p-3 rounded-full shadow-lg">
                    <FiPlay className="w-6 h-6" />
                  </div>
                </div>
              )}
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <motion.h1 
                  className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {speech.title}
                </motion.h1>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Meta Information */}
              <motion.div 
                className="flex flex-wrap items-center gap-4 md:gap-6 text-red-700 mb-6 pb-6 border-b border-red-200"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <FiUser className="w-5 h-5 text-red-600" />
                  <span className="font-medium">{speech.speaker}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-5 h-5 text-red-600" />
                  <span>{formatDate(speech.date)}</span>
                </div>
                {speech.venue && (
                  <div className="flex items-center gap-2">
                    <FiMapPin className="w-5 h-5 text-red-600" />
                    <span>{speech.venue}</span>
                  </div>
                )}
                {speech.duration && (
                  <div className="flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-red-600" />
                    <span>{speech.duration}</span>
                  </div>
                )}
              </motion.div>

              {/* Topics */}
              {speech.topics && speech.topics.length > 0 && (
                <motion.div 
                  className="mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  <h3 className="text-lg font-bold text-red-800 mb-3">
                    {language === 'MR' ? 'विषय:' : 'Topics:'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {speech.topics.map((topic, index) => (
                      <span key={index} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                        {topic}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Speech Content */}
              <motion.div 
                className="prose prose-lg max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                {speech.content && speech.content.map((paragraph, index) => (
                  <p key={index} className="mb-6 text-red-800 leading-relaxed text-lg">
                    {paragraph}
                  </p>
                ))}
              </motion.div>

              {/* Additional Images */}
              {speech.images && speech.images.length > 0 && (
                <motion.div 
                  className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  {speech.images.map((image, index) => (
                    <div key={index} className="rounded-xl overflow-hidden shadow-lg border-2 border-red-100">
                      <img loading="lazy" 
                        src={getImageSrc(image)} 
                        alt={`${speech.title} - Image ${index + 1}`}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Video Section */}
              {speech.videoLink && (
                <motion.div 
                  className="mt-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                >
                  <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                    <FaYoutube className="w-6 h-6 text-red-600" />
                    {language === 'MR' ? 'भाषणाचा व्हिडिओ' : 'Speech Video'}
                  </h3>
                  <button
                    onClick={handlePlayVideo}
                    className="inline-flex items-center gap-3 bg-red-800 hover:bg-red-900 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <FaYoutube className="w-5 h-5" />
                    {language === 'MR' ? 'व्हिडिओ पाहा' : 'Watch Video'}
                  </button>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div 
                className="flex items-center justify-between pt-8 mt-8 border-t border-red-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <div className="flex items-center gap-4">
                  <button 
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      isLiked 
                        ? 'bg-red-800 text-white shadow-lg' 
                        : 'bg-red-100 text-red-800 hover:bg-red-50 hover:text-red-700'
                    }`}
                  >
                    <FiHeart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                    {language === 'MR' ? 'आवडले' : 'Like'}
                  </button>
                  <button 
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-lg font-medium hover:bg-blue-50 hover:text-blue-700 transition-all duration-300"
                  >
                    <FiShare2 className="w-5 h-5" />
                    {language === 'MR' ? 'शेअर करा' : 'Share'}
                  </button>
                </div>
                <Link 
                  to="/allspeeches"
                  className="text-red-800 hover:text-red-900 font-medium transition-colors"
                >
                  {language === 'MR' ? 'सर्व भाषणे पहा' : 'View All Speeches'}
                </Link>
              </motion.div>
            </div>
          </motion.article>

          {/* Related Speeches */}
          {relatedSpeeches.length > 0 && (
            <motion.div 
              className="mt-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-800 mb-8 text-center">
                {language === 'MR' ? 'संबंधित भाषणे' : 'Related Speeches'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedSpeeches.map((relatedSpeech, index) => (
                  <motion.div
                    key={relatedSpeech._id}
                    className="bg-white border-2 border-red-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.6 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                    onClick={() => navigate(`/speech/${relatedSpeech._id}`)}
                  >
                    <div className="relative h-40 overflow-hidden">
                      {relatedSpeech.videoLink && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className="bg-red-800 text-white p-1.5 rounded-full shadow-lg">
                            <FiPlay className="w-3 h-3" />
                          </div>
                        </div>
                      )}
                      <img loading="lazy" 
                        src={getImageSrc(relatedSpeech.thumbnail)} 
                        alt={relatedSpeech.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-red-800 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {relatedSpeech.title}
                      </h3>
                      <p className="text-red-700 text-sm mb-3 line-clamp-2">
                        {Array.isArray(relatedSpeech.content) 
                          ? relatedSpeech.content.join(' ').substring(0, 100) + '...'
                          : relatedSpeech.content?.substring(0, 100) + '...'
                        }
                      </p>
                      <div className="flex items-center justify-between text-xs text-red-600">
                        <span>{relatedSpeech.speaker}</span>
                        <span className="flex items-center gap-1">
                          {relatedSpeech.videoLink ? (
                            <>
                              <FiPlay className="w-3 h-3" />
                              {language === 'MR' ? 'पाहा' : 'Watch'}
                            </>
                          ) : (
                            <>
                              <FiArrowLeft className="w-3 h-3 transform rotate-180" />
                              {language === 'MR' ? 'वाचा' : 'Read'}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && speech.videoLink && (
        <motion.div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden">
            <button 
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-50 text-white hover:text-red-400 transition-colors"
            >
              <FiX className="w-8 h-8" />
            </button>

            <div className="absolute top-4 left-4 z-50">
              <button 
                onClick={toggleMute}
                className="text-white hover:text-red-400 transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-6 h-6" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>
            </div>

            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeId(speech.videoLink)}?autoplay=1&mute=${isMuted ? 1 : 0}&rel=0&modestbranding=1`}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={speech.title}
            />
          </div>
        </motion.div>
      )}

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .prose p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }
        .prose p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default SpeechDetails;